import { Grid } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useRef, useState, lazy, Suspense } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import {
  ChartFrequency,
  CommunicationType,
  ICommunicationAnalytics,
  IJobCandidateInteraction,
} from 'wisestep-api';
import { WsButton, WsColors, WsTypography, WsTypoVariants } from 'wisestep-base';

import WsWorkspaceFilter from '../../../components/filters/components/WsWorkspaceFilter';
import {
  defaultCallAnalyticsValues,
  defaultMISAnalyticsPaylod,
} from '../utils/CallsAnalyticsHelper';

import CareerPageAnalyticsTable from './components/CareerPageAnalyticsTable';
import CommunicationsTable from './components/CommunicationsTable';
import CVViewsAcrossWorkspacesTable from './components/CVViewsAcrossWorkspacesTable';
import DurationDropDown, { IDuration } from './components/DurationDropDown';
import EmailsByAllWorkspacesTable from './components/EmailsByAllWorkspacesTable';
import EmailsWithProviderTable from './components/EmailsWithProviderTable';
import InteractionsWithSource from './components/InteractionsWithSource';
import JobBoardsConfiguredTable from './components/JobBoardsConfiguredTable';
import TotalAppliedCandidatesBySource from './components/MetricCharts/TotalAppliedCandidatesBySource';
import TotalCandidates from './components/MetricCharts/TotalCandidates';
import TotalEmailsSent from './components/MetricCharts/TotalEmailsSent';
import TotalJobsPostedAllTime from './components/MetricCharts/TotalJobsPostedAllTime';
import TotalSMSSentMetric from './components/MetricCharts/TotalSMSSentMetric';
import SearchQueriesPerformedTable from './components/SearchQueriesPerformedTable';
import TasksByAllWorkspacesTable from './components/TasksByAllWorkspacesTable';

const TotalJobsPostedOnJobBoards = lazy(
  () => import('./components/MetricCharts/TotalJobsPostedOnJobBoards')
);

const JobsTrendAcrossWorkspaceTable = lazy(
  () => import('./components/JobsTrendAcrossWorkspaceTable')
);
const JobsPostedOnJobBoardsTable = lazy(() => import('./components/JobsPostedOnJobBoardsTable'));
const CandidatesStats = lazy(() => import('./components/MetricCharts/CandidatesStats'));
const MappingsAndUploadsTable = lazy(() => import('./components/MappingsAndUploadsTable'));
export interface IMISAnalyticsQuery {
  query: ICommunicationAnalytics & IJobCandidateInteraction;
  title?: string;
}
const defaultValues = {
  duration: IDuration.SEVEN_DAYS,
  workspace: '',
};

const InternalMISWorkspaceLevel = () => {
  const methods = useForm({
    defaultValues: defaultValues,
  });

  const { handleSubmit } = methods;

  const [query, setQuery] = useState({
    ...defaultMISAnalyticsPaylod,
    loadAllWorkspacesData: false,
  });
  const selectedWorkspaceId = useRef<string>('');
  const selectedDuration = useRef<IDuration>(defaultValues.duration);

  const onSelect = (data: string) => (selectedWorkspaceId.current = data);
  const onSearch = (data: FieldValues) => {
    selectedDuration.current = data.duration;
    if (data.duration == IDuration.SEVEN_DAYS) {
      query.fromDay = '7d';
      query.frequency = ChartFrequency.DAILY;
    } else if (data.duration == IDuration.THIRTY_DAYS) {
      query.fromDay = '30d';
      query.frequency = ChartFrequency.WEEKLY;
    } else {
      query.fromDay = '';
    }

    setQuery({ ...query, workspaceId: selectedWorkspaceId.current });
  };

  return (
    <FormProvider {...methods}>
      <Grid container component="form" mb={3} onSubmit={handleSubmit(onSearch)}>
        <Grid container bgcolor={WsColors.White} p={2} alignItems="center">
          <Grid item xs={4}>
            <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
              Select workspace name to view data visualizations
            </WsTypography>
          </Grid>
          <Grid container item xs={8} justifyContent="flex-end" spacing={1}>
            <Grid item xs={4}>
              <WsWorkspaceFilter name="workspace" onSelect={onSelect} />
            </Grid>
            <Grid item xs={3} ml={2}>
              <DurationDropDown defaultValueKey={IDuration.SEVEN_DAYS} />
            </Grid>
            <Grid item xs={3} ml={2}>
              <WsButton size="large" type="submit">
                Fetch Analytics
              </WsButton>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {!isEmpty(query.workspaceId) && (
        <Suspense fallback={<Grid>Loading...</Grid>}>
          <TotalJobsPostedAllTime query={query} />
          <TotalJobsPostedOnJobBoards query={query} />
          <JobsTrendAcrossWorkspaceTable query={query} duration={selectedDuration.current} />
          <JobsPostedOnJobBoardsTable query={query} duration={selectedDuration.current} />
          <JobBoardsConfiguredTable workspaceId={selectedWorkspaceId.current} />
          <TotalCandidates workspaceId={selectedWorkspaceId.current} />
          <CandidatesStats query={query} />
          <TotalAppliedCandidatesBySource query={query} duration={selectedDuration.current} />
          <MappingsAndUploadsTable query={query} duration={selectedDuration.current} />
          <SearchQueriesPerformedTable query={query} duration={selectedDuration.current} />
          <InteractionsWithSource
            query={{
              ...query,
              fromDay: query.fromDay ?? '60d',
              jobCandidateMapping: false,
            }}
            title={`Submissions in ${selectedDuration.current ?? 'last 60 Days - Weekly'}`}
          />
          <InteractionsWithSource
            query={{
              ...query,
              fromDay: query.fromDay ?? '60d',
              jobCandidateMapping: true,
            }}
            title={`Mappings in ${selectedDuration.current ?? 'last 60 Days - Weekly'}`}
          />
          <InteractionsWithSource
            query={{
              ...query,
              fromDay: query.fromDay ?? '60d',
              jobCandidateMapping: true,
              candidateStatusTags: ['applied'],
            }}
            title={`Applications received in ${
              selectedDuration.current ?? 'last 60 Days - Weekly'
            }`}
          />
          <CVViewsAcrossWorkspacesTable
            query={{ ...query, fromDay: query.fromDay ?? '60d' }}
            duration={selectedDuration.current}
          />
          <TotalEmailsSent query={query} />
          <EmailsWithProviderTable
            query={{ ...query, fromDay: query.fromDay ?? '60d' }}
            duration={selectedDuration.current}
          />
          <EmailsByAllWorkspacesTable query={query} duration={selectedDuration.current} />
          <TotalSMSSentMetric query={query} />
          <CommunicationsTable
            query={{
              ...defaultCallAnalyticsValues,
              fromDay: query.fromDay ?? '60d',
              type: CommunicationType.SMS,
              loadAllWorkspacesData: false,
              frequency: query.frequency,
              workspaceId: query.workspaceId,
            }}
            duration={selectedDuration.current}
          />
          <CareerPageAnalyticsTable query={query} duration={selectedDuration.current} />
          <TasksByAllWorkspacesTable query={query} duration={selectedDuration.current} />
        </Suspense>
      )}
    </FormProvider>
  );
};

export default InternalMISWorkspaceLevel;
